import React, { useState } from "react";
import {
    Box,
    Typography,
    Divider,
    useMediaQuery,
    useTheme,
    Select,
    MenuItem,
    Paper,
    Grid,
    Card,
    CardContent,
    Stack,
    Avatar
} from "@mui/material";
import {
    School as SchoolIcon,
    Book as BookIcon,
    Psychology as PsychologyIcon,
    Dashboard as DashboardIcon

} from "@mui/icons-material";
import GroupIcon from "@mui/icons-material/Group";
import StudentUploadUI from "./StudentUploadUI";
import StudentProfileForm from "./StudentProfileForm";
import ManageStudentPublicAccess from "./StudentDashboard/ManageStudentPublicAccess";

const ManageStudents = () => {
    const [tabIndex, setTabIndex] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    const tabData = [
        {
            index: 0,
            label: "Student List",
            shortLabel: "AddStudents",
            icon: <SchoolIcon />,
            description: "Add, edit, or delete student records for your classes"
        },
        {
            index: 1,
            label: "Manage Student Access",
            shortLabel: "ReportCards",
            icon: <BookIcon />,
            description: "Create login credentials so students or guardians can securely access progress reports online"
        }
    ];

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.default,
                minHeight: "100vh",
                p: { xs: 2, sm: 3, md: 4 },
                maxWidth: "100%",
                mx: "auto"
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}><GroupIcon /></Avatar>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color="primary">Manage Students</Typography>
                    <Typography variant="body2" color="text.secondary">Add, view, and manage student information.</Typography>
                    <Typography variant="body2" color="text.secondary"><br></br></Typography>
                </Box>
            </Box>

            {/* Navigation */}
            {isMobile ? (
                <Stack spacing={2}>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                            color: theme.palette.primary.dark,
                            textAlign: "center",
                            fontSize: { xs: "1.1rem", sm: "1.25rem" },
                            mb: 1
                        }}
                    >
                        Select Section
                    </Typography>
                    <Select
                        fullWidth
                        value={tabIndex}
                        onChange={(e) => setTabIndex(e.target.value)}
                        sx={{
                            backgroundColor: theme.palette.primary.light,
                            borderRadius: 2,
                            mb: 4,
                            color: theme.palette.primary.contrastText,
                            fontWeight: 600,
                            fontSize: '1rem',
                            '& .MuiSelect-select': { py: 2, px: 2.5 },
                            '& .MuiMenuItem-root': { fontSize: '1rem' }
                        }}
                    >
                        {tabData.map((tab) => (
                            <MenuItem key={tab.index} value={tab.index}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ fontSize: '1.25rem' }}>{tab.icon}</Box>
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>{tab.label}</Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>
            ) : (
                <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 3, sm: 4, md: 5 }, maxWidth: '1200px', mx: 'auto' }}>
                    {tabData.map((tab) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={tab.index}>
                            <Card
                                elevation={tabIndex === tab.index ? 4 : 1}
                                sx={{
                                    height: '240px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    border: tabIndex === tab.index ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                                    borderColor: tabIndex === tab.index ? 'primary.main' : 'divider',
                                    backgroundColor: tabIndex === tab.index ? 'primary.main' : 'background.paper',
                                    color: tabIndex === tab.index ? 'primary.contrastText' : 'inherit',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: 4
                                    }
                                }}
                                onClick={() => setTabIndex(tab.index)}
                            >
                                <CardContent sx={{
                                    textAlign: 'center',
                                    p: { xs: 1.5, sm: 2, md: 2.5 },
                                    '&:last-child': { pb: { xs: 1.5, sm: 2, md: 2.5 } }
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1, sm: 1.5 } }}>
                                        <Box sx={{
                                            p: { xs: 1, sm: 1.5 },
                                            borderRadius: '50%',
                                            backgroundColor: tabIndex === tab.index ? 'primary.contrastText' : 'grey.100',
                                            color: tabIndex === tab.index ? 'primary.main' : 'grey.600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
                                        }}>
                                            {tab.icon}
                                        </Box>
                                    </Box>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                        sx={{
                                            fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                                            color: 'inherit',
                                            mb: { xs: 0.5, sm: 0.75 },
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {tab.label}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                                            color: 'inherit',
                                            opacity: 0.85,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {tab.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Content */}
            <Paper
                elevation={4}
                sx={{
                    borderRadius: { xs: 2, sm: 3 },
                    backgroundColor: theme.palette.background.paper,
                    p: { xs: 1.5, sm: 2, md: 3, lg: 4 },
                    mt: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    minHeight: '60vh'
                }}
            >

                <Box sx={{ '& > *': { width: '100%' } }}>
                    {tabIndex === 0 && <StudentUploadUI />}
                    {tabIndex === 1 && <ManageStudentPublicAccess />}

                </Box>
            </Paper>
        </Box>
    );
};

export default ManageStudents;